import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Toggle } from '../components/ui/Toggle';
import { ArrowLeft, ChevronRight, ChevronDown, Star, ImageOff, CheckCircle, XCircle, Tag, DollarSign, Layers, Info, Edit, X, Globe, MapPin, Building, Search, Undo2, Check, Plus } from 'lucide-react';
type ScopeLevel = 'global' | 'region' | 'community';
type Scope = {
  level: ScopeLevel;
  regionId?: string;
  communityId?: string;
  name: string;
};
type GlobalOption = {
  id: string;
  name: string;
  category: string;
  subcategory: string;
  variantSet: string;
  basePrice: number;
  description: string;
  enabled: boolean;
  isStandard: boolean;
  hasImage: boolean;
  imageUrl?: string;
  tags?: string[];
};
type OptionOverride = {
  optionId: string;
  level: 'region' | 'community';
  levelId: string;
  priceOverride?: number;
  enabledOverride?: boolean;
  nameOverride?: string;
  descriptionOverride?: string;
  isStandardOverride?: boolean;
};
type OptionLevel = 'standard' | 'level-1' | 'level-2' | 'level-3' | null;
const levelConfig: Record<string, {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
}> = {
  standard: {
    label: 'Standard',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200'
  },
  'level-1': {
    label: 'Level 1',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  'level-2': {
    label: 'Level 2',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200'
  },
  'level-3': {
    label: 'Level 3',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  }
};
const initialLevelAssignments: Record<string, OptionLevel> = {
  'ext-1': 'standard',
  'ext-2': 'level-1',
  'int-1': 'level-1',
  'int-2': 'standard',
  'int-3': 'level-2',
  'int-6': 'level-2',
  'int-7': 'standard',
  'int-8': 'level-2',
  'model-1': null
};
// --- Mock Data ---
const regions = [{
  id: 'alberta',
  name: 'Alberta Region'
}, {
  id: 'ontario',
  name: 'Ontario Region'
}, {
  id: 'bc',
  name: 'BC Region'
}, {
  id: 'texas',
  name: 'Texas Region'
}, {
  id: 'florida',
  name: 'Florida Region'
}, {
  id: 'arizona',
  name: 'Arizona Region'
}];
const communities: Record<string, {
  id: string;
  name: string;
}[]> = {
  alberta: [{
    id: 'oakwood',
    name: 'Oakwood Estates'
  }, {
    id: 'maple',
    name: 'Maple Ridge'
  }, {
    id: 'pine',
    name: 'Pine Valley'
  }, {
    id: 'cedar',
    name: 'Cedar Heights'
  }],
  ontario: [{
    id: 'lakeside',
    name: 'Lakeside Living'
  }, {
    id: 'urban',
    name: 'Urban Heights'
  }, {
    id: 'riverdale',
    name: 'Riverdale Commons'
  }],
  bc: [{
    id: 'mountain',
    name: 'Mountain View'
  }, {
    id: 'pacific',
    name: 'Pacific Point'
  }, {
    id: 'coastal',
    name: 'Coastal Breeze'
  }],
  texas: [{
    id: 'austin-hills',
    name: 'Austin Hills'
  }, {
    id: 'dallas-park',
    name: 'Dallas Park'
  }, {
    id: 'houston-meadows',
    name: 'Houston Meadows'
  }],
  florida: [{
    id: 'miami-shores',
    name: 'Miami Shores'
  }, {
    id: 'tampa-bay',
    name: 'Tampa Bay Estates'
  }, {
    id: 'orlando-gardens',
    name: 'Orlando Gardens'
  }],
  arizona: [{
    id: 'phoenix-ridge',
    name: 'Phoenix Ridge'
  }, {
    id: 'scottsdale-heights',
    name: 'Scottsdale Heights'
  }]
};
const allBuilderOptions: GlobalOption[] = [{
  id: 'model-1',
  name: 'The Lawson',
  category: 'Models',
  subcategory: 'The Lawson',
  variantSet: 'Model Details',
  enabled: true,
  isStandard: false,
  basePrice: 600000,
  description: '1795 sq ft • 3 beds • 2.5 baths • Two storey laned home. Features an open-concept main floor with spacious kitchen, living, and dining areas. Upper floor includes a generous primary suite with ensuite and walk-in closet, plus two additional bedrooms and a full bathroom.',
  hasImage: true,
  imageUrl: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80',
  tags: ['Calgary', 'Two Storey', 'Laned']
}, {
  id: 'ext-1',
  name: 'Black Asphalt Shingles',
  category: 'Exterior',
  subcategory: 'Roofing',
  variantSet: 'Shingles',
  enabled: true,
  isStandard: true,
  basePrice: 0,
  description: 'Classic black asphalt shingles with 30-year warranty. Durable and weather-resistant, these shingles provide excellent protection and a clean aesthetic for any home style. Available in a rich, deep black that complements a wide range of exterior color palettes.',
  hasImage: true,
  imageUrl: 'https://cdn.magicpatterns.com/uploads/gy4nLAmMDnxDZrtDgT1xUc/black-asphalt.jpg',
  tags: ['Asphalt', '30-year warranty']
}, {
  id: 'ext-2',
  name: 'Gray Asphalt Shingles',
  category: 'Exterior',
  subcategory: 'Roofing',
  variantSet: 'Shingles',
  enabled: true,
  isStandard: false,
  basePrice: 1200,
  description: 'Modern gray asphalt shingles — an upgrade option that pairs beautifully with lighter siding colors. Features the same 30-year warranty and weather resistance as the standard option.',
  hasImage: true,
  imageUrl: 'https://cdn.magicpatterns.com/uploads/7fXbSwM5gVoriK8Hvp5r78/gray-asphalt.jpg',
  tags: ['Asphalt', 'Modern']
}, {
  id: 'int-1',
  name: 'Stainless Steel Appliances',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Appliance Package',
  enabled: true,
  isStandard: false,
  basePrice: 2500,
  description: 'Premium stainless steel appliance package including refrigerator, range, dishwasher, and over-the-range microwave. Fingerprint-resistant finish keeps your kitchen looking pristine.',
  hasImage: true,
  imageUrl: 'https://cdn.magicpatterns.com/uploads/uz3yKu22Y2YDi2VtcShnwy/stainless-steel-fridge.jpg',
  tags: ['Appliances', 'Stainless']
}, {
  id: 'int-2',
  name: 'Black + White Appliances',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Appliance Package',
  enabled: true,
  isStandard: true,
  basePrice: 0,
  description: 'Classic black and white appliance package — included with every home. Reliable, energy-efficient appliances that cover all your kitchen essentials.',
  hasImage: false,
  tags: ['Appliances', 'Basic']
}, {
  id: 'int-3',
  name: 'Built-in Appliances',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Appliance Package',
  enabled: true,
  isStandard: false,
  basePrice: 8500,
  description: 'Premium built-in appliance package with panel-ready fronts for a seamless, custom kitchen look. Includes built-in refrigerator, wall oven, cooktop, and integrated dishwasher.',
  hasImage: false,
  tags: ['Appliances', 'Built-in', 'Premium']
}, {
  id: 'int-6',
  name: 'White Quartz Countertops',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Countertops',
  enabled: true,
  isStandard: false,
  basePrice: 4500,
  description: 'Classic white quartz countertops with subtle veining. Non-porous, stain-resistant surface that requires no sealing. Heat and scratch resistant for everyday kitchen use.',
  hasImage: false,
  tags: ['Quartz', 'Classic']
}, {
  id: 'int-7',
  name: 'Laminate Countertops',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Countertops',
  enabled: true,
  isStandard: true,
  basePrice: 0,
  description: 'Standard laminate countertops — included with all models. Available in a variety of colors and patterns. Easy to clean and maintain.',
  hasImage: false,
  tags: ['Laminate', 'Standard']
}, {
  id: 'int-8',
  name: 'Natural Oak Hardwood',
  category: 'Interior',
  subcategory: 'Flooring',
  variantSet: 'Hardwood',
  enabled: true,
  isStandard: false,
  basePrice: 8500,
  description: 'Classic natural oak hardwood flooring with a warm, timeless finish. Solid 3/4" planks with a durable polyurethane coating. Adds warmth and character to any room.',
  hasImage: false,
  tags: ['Hardwood', 'Oak', 'Natural']
}];
const initialOverrides: OptionOverride[] = [{
  optionId: 'int-8',
  level: 'region',
  levelId: 'alberta',
  priceOverride: 7200
}, {
  optionId: 'int-1',
  level: 'community',
  levelId: 'oakwood',
  isStandardOverride: true,
  priceOverride: 0
}, {
  optionId: 'int-3',
  level: 'community',
  levelId: 'cedar',
  isStandardOverride: true,
  priceOverride: 0
}, {
  optionId: 'int-6',
  level: 'region',
  levelId: 'ontario',
  priceOverride: 5200
}, {
  optionId: 'ext-2',
  level: 'community',
  levelId: 'maple',
  enabledOverride: false
}];
// --- Edit Modal with Scope Selection ---
function EditOptionModal({
  isOpen,
  onClose,
  option,
  overrides,
  onSaveGlobal,
  onSaveOverride,
  onResetOverride,
  optionLevel,
  onSetLevel
}: {
  isOpen: boolean;
  onClose: () => void;
  option: GlobalOption;
  overrides: OptionOverride[];
  onSaveGlobal: (updates: Partial<GlobalOption>) => void;
  onSaveOverride: (override: OptionOverride) => void;
  onResetOverride: (level: 'region' | 'community', levelId: string) => void;
  optionLevel: OptionLevel;
  onSetLevel: (level: OptionLevel) => void;
}) {
  const [scope, setScope] = useState<Scope>({
    level: 'global',
    name: 'All Options'
  });
  const [scopePickerOpen, setScopePickerOpen] = useState(false);
  const [scopeSearch, setScopeSearch] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<OptionLevel>(optionLevel);
  // Get the existing override for the current scope
  const existingOverride = useMemo(() => {
    if (scope.level === 'global') return null;
    const levelId = scope.level === 'region' ? scope.regionId : scope.communityId;
    return overrides.find(o => o.optionId === option.id && o.level === scope.level && o.levelId === levelId) || null;
  }, [scope, overrides, option.id]);
  // Get the parent value (what this scope inherits from)
  const parentValue = useMemo(() => {
    if (scope.level === 'global') return null;
    let price = option.basePrice;
    let enabled = option.enabled;
    let isStandard = option.isStandard;
    let name = option.name;
    let description = option.description;
    if (scope.level === 'community' && scope.regionId) {
      const regionOverride = overrides.find(o => o.optionId === option.id && o.level === 'region' && o.levelId === scope.regionId);
      if (regionOverride) {
        if (regionOverride.priceOverride !== undefined) price = regionOverride.priceOverride;
        if (regionOverride.enabledOverride !== undefined) enabled = regionOverride.enabledOverride;
        if (regionOverride.isStandardOverride !== undefined) isStandard = regionOverride.isStandardOverride;
        if (regionOverride.nameOverride !== undefined) name = regionOverride.nameOverride;
        if (regionOverride.descriptionOverride !== undefined) description = regionOverride.descriptionOverride;
      }
    }
    return {
      price,
      enabled,
      isStandard,
      name,
      description
    };
  }, [scope, overrides, option]);
  // Form state — initialized from existing override or parent/global
  const initialPrice = existingOverride?.priceOverride?.toString() ?? (parentValue?.price ?? option.basePrice).toString();
  const initialEnabled = existingOverride?.enabledOverride ?? parentValue?.enabled ?? option.enabled;
  const initialStandard = existingOverride?.isStandardOverride ?? parentValue?.isStandard ?? option.isStandard;
  const initialName = existingOverride?.nameOverride ?? parentValue?.name ?? option.name;
  const initialDescription = existingOverride?.descriptionOverride ?? parentValue?.description ?? option.description;
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [price, setPrice] = useState(initialPrice);
  const [enabled, setEnabled] = useState(initialEnabled);
  const [isStandard, setIsStandard] = useState(initialStandard);
  // Reset form when scope changes
  const handleScopeChange = (newScope: Scope) => {
    setScope(newScope);
    setScopePickerOpen(false);
    setScopeSearch('');
    // Recalculate values for new scope
    const newLevelId = newScope.level === 'region' ? newScope.regionId : newScope.communityId;
    const newOverride = newScope.level !== 'global' ? overrides.find(o => o.optionId === option.id && o.level === newScope.level && o.levelId === newLevelId) || null : null;
    let parentPrice = option.basePrice;
    let parentEnabled = option.enabled;
    let parentStandard = option.isStandard;
    let parentName = option.name;
    let parentDesc = option.description;
    if (newScope.level === 'community' && newScope.regionId) {
      const regionOv = overrides.find(o => o.optionId === option.id && o.level === 'region' && o.levelId === newScope.regionId);
      if (regionOv) {
        if (regionOv.priceOverride !== undefined) parentPrice = regionOv.priceOverride;
        if (regionOv.enabledOverride !== undefined) parentEnabled = regionOv.enabledOverride;
        if (regionOv.isStandardOverride !== undefined) parentStandard = regionOv.isStandardOverride;
        if (regionOv.nameOverride !== undefined) parentName = regionOv.nameOverride;
        if (regionOv.descriptionOverride !== undefined) parentDesc = regionOv.descriptionOverride;
      }
    }
    if (newScope.level === 'global') {
      setName(option.name);
      setDescription(option.description);
      setPrice(option.basePrice.toString());
      setEnabled(option.enabled);
      setIsStandard(option.isStandard);
    } else {
      setName(newOverride?.nameOverride ?? parentName);
      setDescription(newOverride?.descriptionOverride ?? parentDesc);
      setPrice((newOverride?.priceOverride ?? parentPrice).toString());
      setEnabled(newOverride?.enabledOverride ?? parentEnabled);
      setIsStandard(newOverride?.isStandardOverride ?? parentStandard);
    }
  };
  if (!isOpen) return null;
  const isGlobal = scope.level === 'global';
  const handleSave = () => {
    // Save level assignment
    onSetLevel(selectedLevel);
    if (isGlobal) {
      onSaveGlobal({
        name,
        description,
        basePrice: parseFloat(price) || 0,
        enabled,
        isStandard
      });
    } else {
      const levelId = scope.level === 'region' ? scope.regionId! : scope.communityId!;
      onSaveOverride({
        optionId: option.id,
        level: scope.level as 'region' | 'community',
        levelId,
        priceOverride: parseFloat(price) || 0,
        enabledOverride: enabled,
        isStandardOverride: isStandard,
        nameOverride: name !== (parentValue?.name ?? option.name) ? name : undefined,
        descriptionOverride: description !== (parentValue?.description ?? option.description) ? description : undefined
      });
    }
    onClose();
  };
  const handleReset = () => {
    if (!isGlobal) {
      const levelId = scope.level === 'region' ? scope.regionId! : scope.communityId!;
      onResetOverride(scope.level as 'region' | 'community', levelId);
      onClose();
    }
  };
  const filteredRegions = scopeSearch ? regions.filter(r => r.name.toLowerCase().includes(scopeSearch.toLowerCase())) : regions;
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-5397">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-5398">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white rounded-t-lg z-10" data-id="element-5399">
          <div data-id="element-5400">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-5401">
              {isGlobal ? 'Edit Option' : 'Override Option'}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-5402">
              {isGlobal ? 'Editing global defaults (affects all regions & communities)' : <>
                  Customizing for{' '}
                  <span className="font-medium text-slate-900" data-id="element-5403">
                    {scope.name}
                  </span>
                </>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-id="element-5404">
            <X className="h-5 w-5" data-id="element-5405" />
          </button>
        </div>

        <div className="p-6 space-y-5" data-id="element-5406">
          {/* Scope Selector */}
          <div data-id="element-5407">
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-5408">
              Editing for
            </label>
            <div className="relative" data-id="element-5409">
              <button onClick={() => setScopePickerOpen(!scopePickerOpen)} className="flex items-center gap-3 w-full px-3 py-2.5 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-left" data-id="element-5410">
                {scope.level === 'global' && <Globe className="h-4 w-4 text-slate-500" data-id="element-5411" />}
                {scope.level === 'region' && <MapPin className="h-4 w-4 text-blue-500" data-id="element-5412" />}
                {scope.level === 'community' && <Building className="h-4 w-4 text-amber-500" data-id="element-5413" />}
                <div className="flex-1" data-id="element-5414">
                  <p className="text-sm font-medium text-slate-900" data-id="element-5415">
                    {scope.name}
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-5416">
                    {scope.level === 'global' && 'Changes apply everywhere'}
                    {scope.level === 'region' && 'Override for this region only'}
                    {scope.level === 'community' && 'Override for this community only'}
                  </p>
                </div>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${scopePickerOpen ? 'rotate-180' : ''}`} data-id="element-5417" />
              </button>

              {scopePickerOpen && <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50 max-h-[300px] flex flex-col" data-id="element-5418">
                  <div className="px-3 pb-2 border-b border-slate-100" data-id="element-5419">
                    <div className="relative" data-id="element-5420">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-5421" />
                      <input type="text" placeholder="Search regions or communities..." value={scopeSearch} onChange={e => setScopeSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-5422" />
                    </div>
                  </div>
                  <div className="overflow-y-auto flex-1" data-id="element-5423">
                    {(!scopeSearch || 'all options'.includes(scopeSearch.toLowerCase())) && <button onClick={() => handleScopeChange({
                  level: 'global',
                  name: 'All Options'
                })} className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-slate-50 ${scope.level === 'global' ? 'bg-slate-50' : ''}`} data-id="element-5424">
                        <Globe className="h-4 w-4 text-slate-600" data-id="element-5425" />
                        <div className="flex-1" data-id="element-5426">
                          <p className="text-sm font-medium text-slate-900" data-id="element-5427">
                            All Options
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-5428">
                            Edit base defaults
                          </p>
                        </div>
                        {scope.level === 'global' && <Check className="h-4 w-4 text-primary-600" data-id="element-5429" />}
                      </button>}
                    <div className="my-1 border-t border-slate-100" data-id="element-5430" />
                    <div className="px-4 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5431">
                      Regions & Communities
                    </div>
                    {filteredRegions.map(region => {
                  const regionComms = communities[region.id] || [];
                  const filteredComms = scopeSearch ? regionComms.filter(c => c.name.toLowerCase().includes(scopeSearch.toLowerCase())) : regionComms;
                  const showRegion = !scopeSearch || region.name.toLowerCase().includes(scopeSearch.toLowerCase()) || filteredComms.length > 0;
                  if (!showRegion) return null;
                  return <div key={region.id} data-id="element-5432">
                          <button onClick={() => handleScopeChange({
                      level: 'region',
                      regionId: region.id,
                      name: region.name
                    })} className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-slate-50 ${scope.level === 'region' && scope.regionId === region.id ? 'bg-blue-50' : ''}`} data-id="element-5433">
                            <MapPin className="h-4 w-4 text-blue-500" data-id="element-5434" />
                            <span className="text-sm font-medium text-slate-900 flex-1" data-id="element-5435">
                              {region.name}
                            </span>
                            {scope.level === 'region' && scope.regionId === region.id && <Check className="h-4 w-4 text-primary-600" data-id="element-5436" />}
                          </button>
                          {filteredComms.map(comm => <button key={comm.id} onClick={() => handleScopeChange({
                      level: 'community',
                      regionId: region.id,
                      communityId: comm.id,
                      name: comm.name
                    })} className={`w-full py-1.5 px-2 ml-10 mr-4 rounded text-left flex items-center gap-2 hover:bg-slate-50 ${scope.level === 'community' && scope.communityId === comm.id ? 'bg-amber-50 text-amber-900' : 'text-slate-600'}`} data-id="element-5437">
                              <Building className="h-3 w-3" data-id="element-5438" />
                              <span className="text-sm flex-1" data-id="element-5439">
                                {comm.name}
                              </span>
                              {scope.level === 'community' && scope.communityId === comm.id && <Check className="h-3 w-3 text-primary-600" data-id="element-5440" />}
                            </button>)}
                        </div>;
                })}
                  </div>
                </div>}
            </div>

            {/* Existing override indicator */}
            {existingOverride && <div className="mt-2 flex items-center gap-2 text-xs" data-id="element-5441">
                <Badge variant="warning" className="text-[10px]" data-id="element-5442">
                  <Edit className="h-3 w-3 mr-0.5" data-id="element-5443" /> Has Override
                </Badge>
                <span className="text-slate-500" data-id="element-5444">
                  This {scope.level} already has custom values
                </span>
              </div>}
          </div>

          {/* Option preview */}
          <div className="bg-slate-50 p-4 rounded-lg flex gap-4" data-id="element-5445">
            {option.hasImage && option.imageUrl ? <img src={option.imageUrl} alt={option.name} className="w-16 h-16 object-cover rounded-md bg-white border border-slate-200" data-id="element-5446" /> : <div className="w-16 h-16 bg-white border border-slate-200 rounded-md flex items-center justify-center" data-id="element-5447">
                <ImageOff className="h-6 w-6 text-slate-300" data-id="element-5448" />
              </div>}
            <div className="flex-1" data-id="element-5449">
              <p className="text-xs text-slate-500" data-id="element-5450">
                {option.category} › {option.subcategory} › {option.variantSet}
              </p>
              <p className="font-medium text-slate-900 mt-0.5" data-id="element-5451">{option.name}</p>
            </div>
          </div>

          {/* Name */}
          <div data-id="element-5452">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5453">
              {isGlobal ? 'Option Name' : 'Override Name'}
            </label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-5454" />
            {!isGlobal && parentValue && name !== parentValue.name && <p className="text-xs text-slate-400 mt-1" data-id="element-5455">
                Parent: {parentValue.name}
              </p>}
          </div>

          {/* Description */}
          <div data-id="element-5456">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5457">
              {isGlobal ? 'Description' : 'Override Description'}
            </label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-5458" />
          </div>

          {/* Price */}
          <div data-id="element-5459">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5460">
              {isGlobal ? 'Base Price' : 'Override Price'}
            </label>
            <div className="flex items-center gap-3" data-id="element-5461">
              <div className="relative flex-1" data-id="element-5462">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-5463">
                  $
                </span>
                <input type="number" value={price} onChange={e => setPrice(e.target.value)} className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-5464" />
              </div>
              {!isGlobal && parentValue && <div className="text-sm text-slate-500" data-id="element-5465">
                  was{' '}
                  <span className="line-through" data-id="element-5466">
                    ${parentValue.price.toLocaleString()}
                  </span>
                </div>}
            </div>
            {isStandard && parseFloat(price) > 0 && <p className="text-xs text-amber-600 mt-1" data-id="element-5467">
                ⚠️ Standard options typically have $0 price since they're
                included.
              </p>}
          </div>

          {/* Level Selector — builder-level only */}
          <div data-id="element-5468">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-5469">
              Option Level
            </label>
            <p className="text-xs text-slate-500 mb-3" data-id="element-5470">
              Categorize this option into a pricing/quality tier for the
              front-end experience.
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-5471">
              <button onClick={() => setSelectedLevel(null)} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${selectedLevel === null ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-5472">
                No Level
              </button>
              {Object.entries(levelConfig).map(([key, config]) => <button key={key} onClick={() => setSelectedLevel(key as OptionLevel)} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${selectedLevel === key ? `${config.bgColor} ${config.color} ${config.borderColor} ring-2 ring-offset-1 ring-${key === 'standard' ? 'emerald' : key === 'level-1' ? 'blue' : key === 'level-2' ? 'amber' : 'purple'}-300` : `bg-white text-slate-600 border-slate-200 hover:${config.bgColor}`}`} data-id="element-5473">
                  {config.label}
                </button>)}
            </div>
          </div>

          {/* Standard toggle */}
          <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50/50" data-id="element-5474">
            <div className="flex items-center justify-between" data-id="element-5475">
              <div data-id="element-5476">
                <label className="block text-sm font-medium text-slate-900" data-id="element-5477">
                  Include as Standard
                </label>
                <p className="text-xs text-slate-500 mt-1" data-id="element-5478">
                  {isStandard ? 'This option is INCLUDED at no extra cost' : 'This option is an UPGRADE (costs extra)'}
                </p>
                {!isGlobal && parentValue && <p className="text-xs text-emerald-600 mt-1" data-id="element-5479">
                    Parent: {parentValue.isStandard ? 'Standard' : 'Upgrade'}
                  </p>}
              </div>
              <Toggle checked={isStandard} onChange={setIsStandard} label={isStandard ? 'Standard' : 'Upgrade'} data-id="element-5480" />
            </div>
          </div>

          {/* Enabled toggle */}
          <div className="flex items-center justify-between" data-id="element-5481">
            <div data-id="element-5482">
              <label className="block text-sm font-medium text-slate-700" data-id="element-5483">
                Availability
              </label>
              <p className="text-xs text-slate-500" data-id="element-5484">
                {isGlobal ? 'Make this option available for selection' : `Override availability for ${scope.name}`}
              </p>
              {!isGlobal && parentValue && <p className="text-xs text-slate-400 mt-0.5" data-id="element-5485">
                  Parent: {parentValue.enabled ? 'Enabled' : 'Disabled'}
                </p>}
            </div>
            <Toggle checked={enabled} onChange={setEnabled} label={enabled ? 'Enabled' : 'Disabled'} data-id="element-5486" />
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-slate-200 sticky bottom-0 bg-white rounded-b-lg" data-id="element-5487">
          <div data-id="element-5488">
            {!isGlobal && existingOverride && <button onClick={handleReset} className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700" data-id="element-5489">
                <Undo2 className="h-4 w-4" data-id="element-5490" /> Reset to Parent
              </button>}
          </div>
          <div className="flex items-center gap-3" data-id="element-5491">
            <Button variant="ghost" onClick={onClose} data-id="element-5492">
              Cancel
            </Button>
            <Button onClick={handleSave} data-id="element-5493">
              {isGlobal ? 'Save Changes' : 'Save Override'}
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
export function OptionDetailPage() {
  const {
    builderId,
    optionId
  } = useParams<{
    builderId: string;
    optionId: string;
  }>();
  const navigate = useNavigate();
  const [options, setOptions] = useState<GlobalOption[]>(allBuilderOptions);
  const [overrides, setOverrides] = useState<OptionOverride[]>(initialOverrides);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [levelAssignments, setLevelAssignments] = useState<Record<string, OptionLevel>>(initialLevelAssignments);
  const [newTagInput, setNewTagInput] = useState('');
  const option = options.find(o => o.id === optionId);
  const optionLevel = optionId ? levelAssignments[optionId] || null : null;
  const levelInfo = optionLevel ? levelConfig[optionLevel] : null;
  const handleSaveGlobal = (updates: Partial<GlobalOption>) => {
    setOptions(prev => prev.map(o => o.id === optionId ? {
      ...o,
      ...updates
    } : o));
  };
  const handleSaveOverride = (override: OptionOverride) => {
    setOverrides(prev => {
      const filtered = prev.filter(o => !(o.optionId === override.optionId && o.level === override.level && o.levelId === override.levelId));
      return [...filtered, override];
    });
  };
  const handleResetOverride = (level: 'region' | 'community', levelId: string) => {
    setOverrides(prev => prev.filter(o => !(o.optionId === optionId && o.level === level && o.levelId === levelId)));
  };
  const handleSetLevel = (level: OptionLevel) => {
    if (optionId) {
      setLevelAssignments(prev => ({
        ...prev,
        [optionId]: level
      }));
    }
  };
  const handleAddTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || !option) return;
    if (option.tags?.includes(trimmed)) return;
    handleSaveGlobal({
      tags: [...(option.tags || []), trimmed]
    });
    setNewTagInput('');
  };
  const handleRemoveTag = (tagToRemove: string) => {
    if (!option) return;
    handleSaveGlobal({
      tags: (option.tags || []).filter(t => t !== tagToRemove)
    });
  };
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag(newTagInput);
    }
    if (e.key === 'Backspace' && !newTagInput && option?.tags?.length) {
      handleRemoveTag(option.tags[option.tags.length - 1]);
    }
  };
  // Count overrides for this option
  const optionOverrides = overrides.filter(o => o.optionId === optionId);
  if (!option) {
    return <div className="max-w-4xl mx-auto space-y-6" data-id="element-5494">
        <Button variant="ghost" onClick={() => navigate(`/${builderId}/options`)} leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-5496" />} data-id="element-5495">
          Back to Options
        </Button>
        <Card data-id="element-5497">
          <CardContent className="py-16 text-center" data-id="element-5498">
            <Layers className="h-12 w-12 text-slate-300 mx-auto mb-4" data-id="element-5499" />
            <h2 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-5500">
              Option not found
            </h2>
            <p className="text-slate-500" data-id="element-5501">
              The option "{optionId}" doesn't exist or has been removed.
            </p>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="max-w-5xl mx-auto space-y-6" data-id="element-5502">
      {option && <EditOptionModal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} option={option} overrides={overrides} onSaveGlobal={handleSaveGlobal} onSaveOverride={handleSaveOverride} onResetOverride={handleResetOverride} optionLevel={optionLevel} onSetLevel={handleSetLevel} data-id="element-5503" />}

      {/* Back nav */}
      <Button variant="ghost" onClick={() => navigate(`/${builderId}/options`)} leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-5505" />} data-id="element-5504">
        Back to Options
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between gap-6" data-id="element-5506">
        <div className="flex items-start gap-6" data-id="element-5507">
          <div className="h-24 w-24 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-5508">
            {option.hasImage && option.imageUrl ? <img src={option.imageUrl} alt={option.name} className="h-full w-full object-cover" data-id="element-5509" /> : <ImageOff className="h-10 w-10 text-slate-300" data-id="element-5510" />}
          </div>
          <div className="flex-1" data-id="element-5511">
            <div className="flex items-center gap-3 mb-1 flex-wrap" data-id="element-5512">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-5513">
                {option.name}
              </h1>
              {option.isStandard && <Badge variant="success" data-id="element-5514">
                  <Star className="h-3 w-3 mr-1" data-id="element-5515" /> Standard
                </Badge>}
              {!option.enabled && <Badge variant="error" data-id="element-5516">
                  <XCircle className="h-3 w-3 mr-1" data-id="element-5517" /> Disabled
                </Badge>}
              {levelInfo && <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${levelInfo.bgColor} ${levelInfo.color} ${levelInfo.borderColor}`} data-id="element-5518">
                  {levelInfo.label}
                </span>}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-3" data-id="element-5519">
              <span data-id="element-5520">{option.category}</span>
              <ChevronRight className="h-3.5 w-3.5 text-slate-400" data-id="element-5521" />
              <span data-id="element-5522">{option.subcategory}</span>
              <ChevronRight className="h-3.5 w-3.5 text-slate-400" data-id="element-5523" />
              <span data-id="element-5524">{option.variantSet}</span>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-5525">
              {option.isStandard ? <span className="text-2xl font-bold text-emerald-600" data-id="element-5526">
                  Included
                </span> : <span className="text-2xl font-bold text-slate-900" data-id="element-5527">
                  ${option.basePrice.toLocaleString()}
                </span>}
              {!option.isStandard && <span className="text-sm text-slate-500" data-id="element-5528">upgrade</span>}
            </div>
          </div>
        </div>
        <Button leftIcon={<Edit className="h-4 w-4" data-id="element-5530" />} onClick={() => setEditModalOpen(true)} data-id="element-5529">
          Edit Option
        </Button>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-3 gap-6" data-id="element-5531">
        {/* Left column — 2/3 */}
        <div className="col-span-2 space-y-6" data-id="element-5532">
          {/* Description */}
          <Card data-id="element-5533">
            <CardHeader data-id="element-5534">
              <CardTitle data-id="element-5535">Description</CardTitle>
            </CardHeader>
            <CardContent data-id="element-5536">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5537">
                {option.description}
              </p>
            </CardContent>
          </Card>

          {/* Category & Classification */}
          <Card data-id="element-5538">
            <CardHeader data-id="element-5539">
              <CardTitle data-id="element-5540">Category & Classification</CardTitle>
            </CardHeader>
            <CardContent data-id="element-5541">
              <div className="flex items-center gap-2 text-sm mb-4" data-id="element-5542">
                <span className="px-2.5 py-1 bg-slate-100 rounded-md font-medium text-slate-700" data-id="element-5543">
                  {option.category}
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-5544" />
                <span className="px-2.5 py-1 bg-slate-100 rounded-md font-medium text-slate-700" data-id="element-5545">
                  {option.subcategory}
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-5546" />
                <span className="px-2.5 py-1 bg-slate-100 rounded-md font-medium text-slate-700" data-id="element-5547">
                  {option.variantSet}
                </span>
              </div>
              <div data-id="element-5548">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-5549">
                  Tags
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-5550">
                  {(option.tags || []).map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-50 border border-slate-200 text-slate-600 rounded-md group" data-id="element-5551">
                      <Tag className="h-3 w-3" data-id="element-5552" />
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)} className="ml-0.5 text-slate-400 hover:text-red-500 transition-colors" data-id="element-5553">
                        <X className="h-3 w-3" data-id="element-5554" />
                      </button>
                    </span>)}
                  <div className="inline-flex items-center" data-id="element-5555">
                    <input type="text" value={newTagInput} onChange={e => setNewTagInput(e.target.value)} onKeyDown={handleTagKeyDown} onBlur={() => {
                    if (newTagInput.trim()) handleAddTag(newTagInput);
                  }} placeholder="Add tag..." className="w-24 px-2 py-1 text-xs border border-dashed border-slate-300 rounded-md focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none placeholder:text-slate-400" data-id="element-5556" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Details */}
          <Card data-id="element-5557">
            <CardHeader data-id="element-5558">
              <CardTitle data-id="element-5559">Pricing Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-5560">
              <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-5561">
                <span className="text-sm text-slate-600" data-id="element-5562">Base Price</span>
                {option.isStandard ? <span className="text-lg font-bold text-emerald-600" data-id="element-5563">
                    Included
                  </span> : <span className="text-lg font-bold text-slate-900" data-id="element-5564">
                    ${option.basePrice.toLocaleString()}
                  </span>}
              </div>
              <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-5565">
                <span className="text-sm text-slate-600" data-id="element-5566">Type</span>
                <span className="text-sm font-medium text-slate-900" data-id="element-5567">
                  {option.isStandard ? 'Standard (Included)' : 'Upgrade'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-5568">
                <span className="text-sm text-slate-600" data-id="element-5569">Availability</span>
                {option.enabled ? <Badge variant="success" data-id="element-5570">
                    <CheckCircle className="h-3 w-3 mr-1" data-id="element-5571" /> Available
                  </Badge> : <Badge variant="error" data-id="element-5572">
                    <XCircle className="h-3 w-3 mr-1" data-id="element-5573" /> Unavailable
                  </Badge>}
              </div>
              <div className="flex items-center justify-between py-3" data-id="element-5574">
                <span className="text-sm text-slate-600" data-id="element-5575">
                  Regional Overrides
                </span>
                {optionOverrides.length > 0 ? <Badge variant="warning" data-id="element-5576">
                    {optionOverrides.length} active
                  </Badge> : <span className="text-sm text-slate-400" data-id="element-5577">None</span>}
              </div>
              {optionOverrides.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-id="element-5578">
                  <p className="text-xs font-medium text-amber-800" data-id="element-5579">
                    Active overrides:
                  </p>
                  {optionOverrides.map((ov, i) => {
                const regionName = regions.find(r => r.id === ov.levelId)?.name;
                const communityName = Object.values(communities).flat().find(c => c.id === ov.levelId)?.name;
                const locationName = regionName || communityName || ov.levelId;
                const changes: string[] = [];
                if (ov.priceOverride !== undefined) changes.push(`$${ov.priceOverride.toLocaleString()}`);
                if (ov.enabledOverride !== undefined) changes.push(ov.enabledOverride ? 'Enabled' : 'Disabled');
                if (ov.isStandardOverride !== undefined) changes.push(ov.isStandardOverride ? 'Standard' : 'Upgrade');
                return <div key={i} className="flex items-center justify-between text-xs" data-id="element-5580">
                        <div className="flex items-center gap-1.5" data-id="element-5581">
                          {ov.level === 'region' ? <MapPin className="h-3 w-3 text-blue-500" data-id="element-5582" /> : <Building className="h-3 w-3 text-amber-500" data-id="element-5583" />}
                          <span className="text-amber-900 font-medium" data-id="element-5584">
                            {locationName}
                          </span>
                        </div>
                        <span className="text-amber-700" data-id="element-5585">
                          {changes.join(' · ')}
                        </span>
                      </div>;
              })}
                </div>}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2" data-id="element-5586">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-5587" />
                <p className="text-sm text-blue-800" data-id="element-5588">
                  Use the Edit button to create or modify overrides for specific
                  regions and communities.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column — 1/3 */}
        <div className="space-y-6" data-id="element-5589">
          {/* Image */}
          <Card data-id="element-5590">
            <CardHeader data-id="element-5591">
              <CardTitle data-id="element-5592">Image</CardTitle>
            </CardHeader>
            <CardContent data-id="element-5593">
              {option.hasImage && option.imageUrl ? <div className="rounded-lg overflow-hidden border border-slate-200" data-id="element-5594">
                  <img src={option.imageUrl} alt={option.name} className="w-full h-48 object-cover" data-id="element-5595" />
                </div> : <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 h-48 flex flex-col items-center justify-center gap-2" data-id="element-5596">
                  <ImageOff className="h-8 w-8 text-slate-300" data-id="element-5597" />
                  <p className="text-sm text-slate-400" data-id="element-5598">No image uploaded</p>
                  <Button variant="outline" size="sm" data-id="element-5599">
                    Upload Image
                  </Button>
                </div>}
            </CardContent>
          </Card>

          {/* Quick Info */}
          <Card data-id="element-5600">
            <CardHeader data-id="element-5601">
              <CardTitle data-id="element-5602">Quick Info</CardTitle>
            </CardHeader>
            <CardContent data-id="element-5603">
              <div className="space-y-3" data-id="element-5604">
                <div className="flex items-center justify-between" data-id="element-5605">
                  <span className="text-sm text-slate-500" data-id="element-5606">Option ID</span>
                  <span className="text-xs font-mono text-slate-700 bg-slate-100 px-2 py-0.5 rounded" data-id="element-5607">
                    {option.id}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-5608">
                  <span className="text-sm text-slate-500" data-id="element-5609">Level</span>
                  {levelInfo ? <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${levelInfo.bgColor} ${levelInfo.color} ${levelInfo.borderColor}`} data-id="element-5610">
                      {levelInfo.label}
                    </span> : <span className="text-sm text-slate-400" data-id="element-5611">Unassigned</span>}
                </div>
                <div className="flex items-center justify-between" data-id="element-5612">
                  <span className="text-sm text-slate-500" data-id="element-5613">Category</span>
                  <span className="text-sm text-slate-700" data-id="element-5614">
                    {option.category}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-5615">
                  <span className="text-sm text-slate-500" data-id="element-5616">Subcategory</span>
                  <span className="text-sm text-slate-700" data-id="element-5617">
                    {option.subcategory}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-5618">
                  <span className="text-sm text-slate-500" data-id="element-5619">Variant Set</span>
                  <span className="text-sm text-slate-700" data-id="element-5620">
                    {option.variantSet}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-5621">
                  <span className="text-sm text-slate-500" data-id="element-5622">Status</span>
                  {option.enabled ? <Badge variant="success" data-id="element-5623">Enabled</Badge> : <Badge variant="error" data-id="element-5624">Disabled</Badge>}
                </div>
                <div className="flex items-center justify-between" data-id="element-5625">
                  <span className="text-sm text-slate-500" data-id="element-5626">Type</span>
                  <span className="text-sm text-slate-700" data-id="element-5627">
                    {option.isStandard ? 'Standard' : 'Upgrade'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card data-id="element-5628">
            <CardHeader data-id="element-5629">
              <CardTitle data-id="element-5630">Tags</CardTitle>
            </CardHeader>
            <CardContent data-id="element-5631">
              <div className="flex flex-wrap gap-2" data-id="element-5632">
                {(option.tags || []).map(tag => <span key={tag} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-50 border border-slate-200 text-slate-700 rounded-lg group" data-id="element-5633">
                    <Tag className="h-3.5 w-3.5 text-slate-400" data-id="element-5634" />
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)} className="ml-0.5 text-slate-400 hover:text-red-500 transition-colors" data-id="element-5635">
                      <X className="h-3.5 w-3.5" data-id="element-5636" />
                    </button>
                  </span>)}
              </div>
              <div className="mt-3 flex gap-2" data-id="element-5637">
                <input type="text" value={newTagInput} onChange={e => setNewTagInput(e.target.value)} onKeyDown={handleTagKeyDown} onBlur={() => {
                if (newTagInput.trim()) handleAddTag(newTagInput);
              }} placeholder="Type a tag and press Enter" className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none placeholder:text-slate-400" data-id="element-5638" />
                <Button variant="outline" size="sm" onClick={() => handleAddTag(newTagInput)} disabled={!newTagInput.trim()} leftIcon={<Plus className="h-3.5 w-3.5" data-id="element-5640" />} data-id="element-5639">
                  Add
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-2" data-id="element-5641">
                Press Enter or comma to add. Backspace to remove the last tag.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}